import React, { useState, useEffect, useMemo, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { useForm } from "../hooks/useForm";
import { useUnsavedChanges } from "../hooks/useUnsavedChanges";
import { fetchCategories } from "../service/categoryService";
import { updateSubscription, fetchSubscriptionById } from "../service/subscriptionService";
import { getRestaurantId } from "../utils/JwtUtil";
import SubscriptionTabs from "../components/subscription/SubscriptionTabs";
import SubscriptionContent from "../components/subscription/SubscriptionContent";
import SubscriptionEditHeader from "../components/subscription/SubscriptionEditHeader";

export default function EditSubscription() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const restaurantId = getRestaurantId();
  const qc = useQueryClient();
  const [activeTab, setActiveTab] = useState('basic');

  const { data: categories = [] } = useQuery({
    queryKey: ["categories"],
    queryFn: fetchCategories,
  });

  const { data: subscription, isLoading: isSubscriptionLoading } = useQuery({
    queryKey: ["subscription", id],
    queryFn: () => fetchSubscriptionById(id),
    enabled: !!id,
  });

  // Memoize initial values to prevent unnecessary re-renders
  const initialValues = useMemo(() => {
    if (!subscription) {
      return {
        nameEn: "",
        nameAr: "",
        descriptionEn: "",
        descriptionAr: "",
        expiryDays: "",
        mealsPerDay: "",
        pricePerDay: "",
        offerPercentage1Week: "0",
        offerPercentage2Weeks: "0",
        offerPercentage4Weeks: "0",
        offerPercentage8Weeks: "0",
        imageUrl: "",
        isActive: true,
        mealSpecifications: [],
      };
    }
    return {
      nameEn: subscription.nameEn || "",
      nameAr: subscription.nameAr || "",
      descriptionEn: subscription.descriptionEn || "",
      descriptionAr: subscription.descriptionAr || "",
      expiryDays: subscription.expiryDays?.toString() || "",
      mealsPerDay: subscription.mealsPerDay?.toString() || "",
      pricePerDay: subscription.pricePerDay?.toString() || "",
      offerPercentage1Week: subscription.offerPercentage1Week?.toString() || "0",
      offerPercentage2Weeks: subscription.offerPercentage2Weeks?.toString() || "0",
      offerPercentage4Weeks: subscription.offerPercentage4Weeks?.toString() || "0",
      offerPercentage8Weeks: subscription.offerPercentage8Weeks?.toString() || "0",
      imageUrl: subscription.imageUrl || "",
      isActive: subscription.isActive ?? true,
      mealSpecifications: subscription.mealSpecifications || [],
    };
  }, [subscription]);

  const { values, setField, reset } = useForm(initialValues);

  // Track unsaved changes
  const { hasUnsavedChanges, markAsSaved } = useUnsavedChanges(values, initialValues);

  // Track if we've already initialized the form to prevent constant resets
  const initializedRef = useRef(false);

  // Reset form when subscription data is loaded (only once)
  useEffect(() => {
    if (subscription && !initializedRef.current) {
      reset(initialValues);
      initializedRef.current = true;
    }
  }, [subscription, reset, initialValues]);

  const addMealSpecification = () => {
    setField("mealSpecifications", [
      ...(values.mealSpecifications || []),
      { mealType: "BREAKFAST", numberOfMeals: 1, allowedCategoryIds: [] },
    ]);
  };

  const removeMealSpecification = (index) => {
    setField(
      "mealSpecifications",
      (values.mealSpecifications || []).filter((_, i) => i !== index)
    );
  };

  const updateMealSpecification = (index, field, value) => {
    const updated = (values.mealSpecifications || []).map((spec, i) =>
      i === index ? { ...spec, [field]: value } : spec
    );
    setField("mealSpecifications", updated);
  };

  const subMutation = useMutation({
    mutationFn: (payload) => updateSubscription(id, payload),
    onSuccess: () => {
      markAsSaved(); // Mark changes as saved
      qc.invalidateQueries(["subscriptions", restaurantId]);
      qc.invalidateQueries(["subscription", id]);
      navigate("/subscriptions");
    },
  });

  const handleSubmit = (e) => {
    if (e) e.preventDefault();
    subMutation.mutate({
      nameEn: values.nameEn,
      nameAr: values.nameAr,
      descriptionEn: values.descriptionEn,
      descriptionAr: values.descriptionAr,
      expiryDays: Number(values.expiryDays),
      mealsPerDay: Number(values.mealsPerDay),
      pricePerDay: Number(values.pricePerDay),
      offerPercentage1Week: Number(values.offerPercentage1Week),
      offerPercentage2Weeks: Number(values.offerPercentage2Weeks),
      offerPercentage4Weeks: Number(values.offerPercentage4Weeks),
      offerPercentage8Weeks: Number(values.offerPercentage8Weeks),
      imageUrl: values.imageUrl,
      isActive: values.isActive,
      restaurantId,
      mealSpecifications: values.mealSpecifications || [],
    });
  };

  const handleCancel = () => {
    navigate("/subscriptions");
  };

  if (isSubscriptionLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!subscription) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Subscription not found</h2>
          <button
            onClick={() => navigate("/subscriptions")}
            className="text-blue-600 hover:text-blue-500"
          >
            Back to Subscriptions
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Sticky Header */}
      <SubscriptionEditHeader
        onSave={handleSubmit}
        onCancel={handleCancel}
        saving={subMutation.isPending}
        hasUnsavedChanges={hasUnsavedChanges}
        title="editSubscription"
      />

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Main Card */}
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
          {/* Tabs Header */}
          <div className="bg-gradient-to-r from-white to-gray-50 px-8 pt-6 pb-0">
            <SubscriptionTabs 
              activeTab={activeTab}
              onTabChange={setActiveTab}
            />
          </div>

          {/* Form Content */}
          <form onSubmit={handleSubmit} className="px-8 pb-8">
            <div className="min-h-[600px] py-6">
              <SubscriptionContent
                activeTab={activeTab}
                values={values}
                setField={setField}
                categories={categories}
                addMealSpecification={addMealSpecification}
                removeMealSpecification={removeMealSpecification}
                updateMealSpecification={updateMealSpecification}
                t={t}
              />
            </div>
          </form>
        </div>

        {/* Footer Spacing */}
        <div className="h-8"></div>
      </div>
    </div>
  );
}